import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { Skeleton } from '../components/ui/skeleton';
import { RotateCcw, Clock } from 'lucide-react';
import { generatePlayerSlug } from './PlayerProfile';

// Calculate streaks from matches
const calculateStreaks = (matches, playerId) => {
  const playerMatches = matches
    .filter(m => 
      m.team_a_player1_id === playerId ||
      m.team_a_player2_id === playerId ||
      m.team_b_player1_id === playerId ||
      m.team_b_player2_id === playerId
    )
    .sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(a.created_at);
      const dateB = b.date ? new Date(b.date) : new Date(b.created_at);
      return dateB - dateA;
    });

  if (playerMatches.length === 0) return { wins: 0, losses: 0 };

  let winStreak = 0;
  let lossStreak = 0;

  for (const match of playerMatches) {
    const isTeamA = match.team_a_player1_id === playerId || match.team_a_player2_id === playerId;
    const won = (isTeamA && match.winner_team === 'A') || (!isTeamA && match.winner_team === 'B');
    
    if (winStreak === 0 && lossStreak === 0) {
      if (won) winStreak = 1;
      else lossStreak = 1;
    } else if (winStreak > 0) {
      if (won) winStreak++;
      else break;
    } else if (lossStreak > 0) {
      if (!won) lossStreak++;
      else break;
    }
  }

  return { wins: winStreak, losses: lossStreak };
};

// Desktop RankBadge - rank change next to rank number
const RankBadgeDesktop = ({ rank, rankChange }) => {
  const badgeClass = rank === 1 ? 'rank-1' : rank === 2 ? 'rank-2' : rank === 3 ? 'rank-3' : 'rank-default';
  return (
    <div className="flex items-center gap-1">
      <span className={`rank-badge ${badgeClass}`} data-testid={`rank-${rank}`}>
        {rank}
      </span>
      {rankChange !== 0 && (
        <span className={`text-[10px] font-medium ${
          rankChange > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
        }`}>
          {rankChange > 0 ? '↑' : '↓'}{Math.abs(rankChange)}
        </span>
      )}
    </div>
  );
};

// Mobile RankBadge - rank change below rank number
const RankBadgeMobile = ({ rank, rankChange }) => {
  const badgeClass = rank === 1 ? 'rank-1' : rank === 2 ? 'rank-2' : rank === 3 ? 'rank-3' : 'rank-default';
  const change = rankChange || 0;
  return (
    <div className="flex flex-col items-center">
      <span className={`rank-badge ${badgeClass}`} data-testid={`rank-${rank}`}>
        {rank}
      </span>
      {change !== 0 && (
        <span className={`text-[9px] font-medium mt-0.5 ${
          change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
        }`}>
          {change > 0 ? '↑' : '↓'}{Math.abs(change)}
        </span>
      )}
    </div>
  );
};

const StreakIndicator = ({ streak }) => {
  if (streak.wins >= 3) {
    return (
      <span className="text-[9px] font-medium text-muted-foreground/70">
        ↑{streak.wins}W
      </span>
    );
  }
  if (streak.losses >= 2) {
    return (
      <span className="text-[9px] font-medium text-muted-foreground/70">
        ↓{streak.losses}L
      </span>
    );
  }
  return null;
};

// Unranked Player Row for Desktop
const UnrankedRowDesktop = ({ player, players, t }) => (
  <tr className="bg-muted/30">
    <td>
      <span className="text-muted-foreground">-</span>
    </td>
    <td>
      <Link 
        to={`/p/${player.player_id}/${generatePlayerSlug(player.player_name, players)}`}
        className="font-medium text-muted-foreground hover:text-primary transition-colors"
      >
        {player.player_name}
      </Link>
    </td>
    <td className="text-center text-muted-foreground">{player.total_points}</td>
    <td className="text-center text-muted-foreground">
      {player.games_difference > 0 ? '+' : ''}{player.games_difference}
    </td>
    <td className="text-center text-muted-foreground">{player.matches_played}</td>
    <td colSpan={3} className="text-center">
      <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center justify-center gap-1">
        <Clock className="h-3 w-3" />
        {t('needsMoreMatches').replace('{0}', player.matches_needed)}
      </span>
    </td>
  </tr>
);

// Unranked Player Card for Mobile
const UnrankedCardMobile = ({ player, players, t }) => (
  <div className="bg-muted/30 rounded-lg border border-border p-4">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 pt-0.5">
        <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
          -
        </span>
      </div>
      
      <div className="flex-1 min-w-0">
        <Link 
          to={`/p/${player.player_id}/${generatePlayerSlug(player.player_name, players)}`}
          className="font-semibold text-muted-foreground hover:text-primary transition-colors"
        >
          {player.player_name}
        </Link>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t('needsMoreMatches').replace('{0}', player.matches_needed)}
          </span>
        </div>
      </div>
      
      <div className="flex-shrink-0 text-right">
        <span className="text-sm text-muted-foreground">{player.matches_played} {t('matches').toLowerCase()}</span>
      </div>
    </div>
  </div>
);

export default function Leaderboard() {
  const { t, language } = useLanguage();
  const { leaderboard, matches, players, ladder, loading } = useLadder();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leaderboard-loading">
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  // Handle both old (array) and new (object with qualified/unranked) response formats
  const qualified = Array.isArray(leaderboard) ? leaderboard : (leaderboard?.qualified || []);
  const unranked = Array.isArray(leaderboard) ? [] : (leaderboard?.unranked || []);

  // Create a lookup for player mu/sigma data
  const playerDataLookup = players.reduce((acc, p) => {
    acc[p.id] = { mu: p.mu, sigma: p.sigma, name: p.name };
    return acc;
  }, {});

  // Calculate streaks for qualified players and add mu/sigma
  const qualifiedWithStreaks = qualified.map(player => ({
    ...player,
    streak: calculateStreaks(matches, player.player_id),
    mu: playerDataLookup[player.player_id]?.mu,
    sigma: playerDataLookup[player.player_id]?.sigma
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leaderboard-page">
      {/* Hint for new users */}
      <p className="text-sm text-muted-foreground text-center mb-4">
        {language === 'el' ? 'Πάτησε σε ένα όνομα για να δεις τα στατιστικά του' : 'Tap a player name to see their stats'}
      </p>
      
      {/* Desktop Table */}
      <div className="hidden md:block" data-testid="leaderboard-table">
        {/* Qualified Players Section */}
        <div className="bg-card rounded-xl border border-border overflow-hidden mb-6">
          <table className={`leaderboard-table ${language === 'el' ? 'lang-el' : ''}`}>
            <thead>
              <tr>
                <th className="w-20">{t('rank')}</th>
                <th>{t('player')}</th>
                <th className="text-center">{t('points')}</th>
                <th className="text-center">{t('gamesDiffFull')}</th>
                <th className="text-center">{t('matches')}</th>
                <th className="text-center">{t('wins')}</th>
                <th className="text-center">{t('losses')}</th>
                <th className="text-center">{t('uniquePartnersFull')}</th>
              </tr>
            </thead>
            <tbody>
              {qualifiedWithStreaks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-muted-foreground">
                    {t('noQualifiedPlayers')}
                  </td>
                </tr>
              ) : (
                qualifiedWithStreaks.map((player, index) => {
                  const nameParts = player.player_name.split(' ');
                  const firstName = nameParts[0];
                  const lastName = nameParts.slice(1).join(' ');
                  
                  return (
                    <tr 
                      key={player.player_id} 
                      className={`${index < 3 ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}`}
                      data-testid={`leaderboard-row-${index}`}
                    >
                      <td>
                        <RankBadgeDesktop rank={player.rank} rankChange={player.rank_change || 0} />
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/p/${player.player_id}/${generatePlayerSlug(player.player_name, players)}`}
                            className="hover:text-primary transition-colors"
                          >
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-foreground">{firstName}</span>
                                {ladder?.defending_champion_id === player.player_id && (
                                  <span className="text-amber-500 font-bold text-sm" title={t('defendingChampion')}>Ⓒ</span>
                                )}
                                <StreakIndicator streak={player.streak} />
                              </div>
                              <span className="text-sm text-muted-foreground">{lastName}</span>
                            </div>
                          </Link>
                        </div>
                      </td>
                      <td className="text-center font-semibold text-foreground">
                        {player.total_points}
                      </td>
                      <td className="text-center text-muted-foreground">
                        {player.games_difference > 0 ? '+' : ''}{player.games_difference}
                      </td>
                      <td className="text-center text-muted-foreground">{player.matches_played}</td>
                      <td className="text-center text-green-600 dark:text-green-400 font-medium">{player.wins}</td>
                      <td className="text-center text-red-500 dark:text-red-400">{player.losses}</td>
                      <td className="text-center text-muted-foreground">{player.unique_partners}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Unranked Players Section */}
        {unranked.length > 0 && (
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-muted/50">
              <h3 className="font-semibold text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t('unrankedPlayers')}
              </h3>
            </div>
            <table className={`leaderboard-table ${language === 'el' ? 'lang-el' : ''}`}>
              <thead>
                <tr>
                  <th className="w-20">{t('rank')}</th>
                  <th>{t('player')}</th>
                  <th className="text-center">{t('points')}</th>
                  <th className="text-center">{t('gamesDiffFull')}</th>
                  <th className="text-center">{t('matches')}</th>
                  <th colSpan={3} className="text-center">{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {unranked.map((player) => (
                  <UnrankedRowDesktop 
                    key={player.player_id} 
                    player={player} 
                    players={players}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile List */}
      <div className="md:hidden space-y-2" data-testid="leaderboard-mobile">
        {qualifiedWithStreaks.length === 0 && unranked.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground bg-card rounded-xl border border-border">
            {t('noPlayers')}
          </div>
        ) : (
          <>
            {/* Qualified Players */}
            {qualifiedWithStreaks.map((player, index) => {
              const nameParts = player.player_name.split(' ');
              const firstName = nameParts[0];
              const lastName = nameParts.slice(1).join(' ');
              
              return (
                <div 
                  key={player.player_id} 
                  className="bg-card rounded-lg border border-border p-4"
                  data-testid={`mobile-row-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-0.5">
                      <RankBadgeMobile rank={player.rank} rankChange={player.rank_change || 0} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/p/${player.player_id}/${generatePlayerSlug(player.player_name, players)}`}
                        className="block hover:text-primary transition-colors"
                      >
                        {/* First line: First name */}
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-foreground">{firstName}</span>
                          {ladder?.defending_champion_id === player.player_id && (
                            <span className="text-amber-500 font-bold text-sm" title={t('defendingChampion')}>Ⓒ</span>
                          )}
                          <StreakIndicator streak={player.streak} />
                        </div>
                        {/* Second line: Last name */}
                        <span className="text-sm text-muted-foreground">{lastName}</span>
                      </Link>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground/90 dark:text-muted-foreground/80">
                        <span>{player.wins}{t('winsShort')}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <span>{player.losses}{t('lossesShort')}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <span>+{player.unique_partners}{t('uniquePartners')}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <span className={
                          player.games_difference >= 0 
                            ? 'text-green-600 dark:text-green-500' 
                            : 'text-red-500 dark:text-red-400'
                        }>
                          {player.games_difference > 0 ? '+' : ''}{player.games_difference}{t('gamesDiff')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      <span className="font-bold text-lg text-foreground">{player.total_points}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Unranked Players */}
            {unranked.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {t('unrankedPlayers')}
                  </h3>
                </div>
                {unranked.map((player) => (
                  <UnrankedCardMobile 
                    key={player.player_id} 
                    player={player} 
                    players={players}
                    t={t}
                  />
                ))}
              </>
            )}
            
            {/* Rotate hint */}
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <RotateCcw className="h-4 w-4" />
              <span>{language === 'el' ? 'Περιστρέψτε για αναλυτική κατάταξη' : 'Rotate for detailed leaderboard'}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
